
import { useEffect, useRef } from 'react';
import './VisionPage.css';

const VisionPage = () => {
  const timelineItemsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px'
      }
    );

    // Observe each timeline item with a staggered delay
    timelineItemsRef.current.forEach((item, index) => {
      if (item) {
        // Set a staggered delay based on index
        (item as HTMLElement).style.transitionDelay = `${index * 0.15}s`;
        observer.observe(item);
      }
    });

    return () => {
      timelineItemsRef.current.forEach(item => {
        if (item) observer.unobserve(item);
      });
    };
  }, []);

  const addToRefs = (el: HTMLDivElement | null, index: number) => {
    if (el && !timelineItemsRef.current.includes(el)) {
      timelineItemsRef.current[index] = el;
    }
  };
  return (
    <div className="vision-page">
      <section className="vision-hero">
        <div className="container">
          <h1>Our Vision & Mission</h1>
          <p>Building a better future for the Kayamandi community</p>
        </div>
      </section>
      
      <section className="vision-mission-section">
        <div className="container">
          <div className="vision-mission-grid">
            <div className="vision-mission-card">
              <div className="vision-mission-icon">
                <i className="fas fa-eye"></i>
              </div>
              <h2>Our Vision</h2>
              <p>
                A society where women and children can live with dignity, equality, and opportunity, 
                free from violence and poverty. We envision a Kayamandi where every individual has 
                access to education, healthcare, and the resources needed to thrive.
              </p>
            </div>
            
            <div className="vision-mission-card">
              <div className="vision-mission-icon">
                <i className="fas fa-bullseye"></i>
              </div>
              <h2>Our Mission</h2>
              <p>
                To empower women and children in Kayamandi through comprehensive support services, 
                education, and community development programs that foster self-reliance, resilience, 
                and positive social change.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      <section className="about-section">
        <div className="container">
          <div className="section-header">
            <h2>KWCDP Background</h2>
            <p>Our journey and impact in the Kayamandi community</p>
          </div>
          
          <div className="about-content">
            <div className="about-text">
              <p className="highlight">
                We are proud to be the only NPO in the Kayamandi Township that is headed by a resident of the
                township. This allows us to provide culturally sensitive and community-driven support to those who
                need it most.
              </p>
              
              <div className="about-section">
                <h3>Our Current Situation</h3>
                <p>
                  Although we had to close our Safe House and services to victims due to lack of a suitable building, we
                  continue to operate our Community Nutritional Development Center (CNDC). The CNDC provides
                  nutritious meals to over 250 people daily, excluding weekends, serving the people of Kayamandi and
                  surrounding areas.
                </p>
              </div>
              
              <div className="about-section">
                <h3>New Developments</h3>
                <ul className="feature-list">
                  <li>Raising awareness during Child Protection Week</li>
                  <li>16 Days of Activism against Gender-Based Violence</li>
                  <li>Educating young women about abuse and how to report it</li>
                  <li>Celebrating Women's Day with the community's elderly women</li>
                </ul>
                <p>
                  These programs are a revival of our previous initiatives, and we are excited to bring them back to the
                  community.
                </p>
              </div>
              
              <div className="about-section">
                <h3>Empowering Through Development</h3>
                <p>
                  We are committed to creating a pathway for our beneficiaries to access skills development, training, and
                  poverty reduction initiatives. Our goal is to equip them with the tools and resources needed to improve
                  their socio-economic status and break the cycle of poverty.
                </p>
              </div>
            
                
                <div className="growth-impact">
                  <h4>Growth and Impact</h4>
                  <ul>
                    <li>Our CNDC has grown significantly since its inception, from serving 32 homeless people to the current number of 250-300 individuals</li>
                    <li>The demand for our services continues to increase, particularly in light of the pandemic and rising unemployment rates</li>
                  </ul>
                  <p>
                    By providing these services, KWCDP aims to address the nutritional and social needs of the community,
                    promoting overall well-being and resilience.
                  </p>
                </div>
              
            </div>
          </div>
        </div>
      </section>
      
      <section className="history-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Journey</h2>
            <p>From humble beginnings to making a lasting impact</p>
          </div>
          
          <div className="timeline">
            <div className="timeline-item" ref={el => addToRefs(el, 0)}>
              <div className="timeline-year">2007</div>
              <div className="timeline-content">
                <h3>Foundation</h3>
                <p>KWCDP was founded in August with the support of the Kayamandi CPF and Police Station, starting as a trauma room providing counseling and support to victims of domestic violence and rape.</p>
              </div>
            </div>
            
            <div className="timeline-item" ref={el => addToRefs(el, 1)}>
              <div className="timeline-year">2009</div>
              <div className="timeline-content">
                <h3>Official Launch & Creche Opening</h3>
                <p>Officially launched with the assistance of South African Police and Community Safety. In November, opened the Sibogile Creche & After Care to provide a safe environment for young children.</p>
              </div>
            </div>
            
            <div className="timeline-item" ref={el => addToRefs(el, 2)}>
              <div className="timeline-year">2010</div>
              <div className="timeline-content">
                <h3>Safe House Establishment</h3>
                <p>Expanded into a safe house accommodating up to 8 children and women at a time, providing temporary housing, skills training, and counseling for reintegration into the community.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 3)}>
              <div className="timeline-year">2012</div>
              <div className="timeline-content">
                <h3>NPO Registration</h3>
                <p>Registered as a Non-Profit Organization with the assistance of the Stellenbosch Municipality Community, formalizing our operations and governance structure.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 4)}>
              <div className="timeline-year">2015</div>
              <div className="timeline-content">
                <h3>Nutritional Development Centre</h3>
                <p>Launched the Community Nutritional Development Centre (CNDC), providing nutritious meals to 250-300 people daily, with funding from the Department of Social Development.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 5)}>
              <div className="timeline-year">2018</div>
              <div className="timeline-content">
                <h3>Community Engagement</h3>
                <p>Received kitchen appliances from Corobrick for Sibongile Creche. Team members attended a workshop on marketing and fundraising sponsored by Medical Clinic Southern Africa. Organized a march against crime targeting women and children.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 6)}>
              <div className="timeline-year">2020</div>
              <div className="timeline-content">
                <h3>COVID-19 Response</h3>
                <p>Partnered with the Department of Social Development to distribute 1,200 food parcels to beneficiaries and community members during the COVID-19 lockdown, providing essential support during the pandemic.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 7)}>
              <div className="timeline-year">2021</div>
              <div className="timeline-content">
                <h3>Relocation</h3>
                <p>Relocated due to Stellenbosch Municipality's plans to build a taxi rank, resulting in the closure of the crèche and safe house due to space constraints.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 8)}>
              <div className="timeline-year">2022</div>
              <div className="timeline-content">
                <h3>Expanding Food Security</h3>
                <p>Became a beneficiary of Food Forward SA's food share program, enhancing our ability to provide consistent food support to the Kayamandi community. This partnership significantly expanded our capacity to address food insecurity in the area.</p>
              </div>
            </div>

            <div className="timeline-item" ref={el => addToRefs(el, 9)}>
              <div className="timeline-year">2023</div>
              <div className="timeline-content">
                <h3>Academic Partnerships & Support</h3>
                <p>Established valuable partnerships with the University of Stellenbosch, including student donations of sanitary pads and toiletries. Collaborated with the Department of Chemical Engineering's Postgraduate Student Council to create a sustainable vegetable garden and distribute essential items to community members in need.</p>
              </div>
            </div>

            <div className="timeline-item future" ref={el => addToRefs(el, 10)}>
              <div className="timeline-year">Future</div>
              <div className="timeline-content">
                <h3>Our Vision for Tomorrow</h3>
                <p>Acquiring a new facility to re-establish our safe house and crèche for domestic violence and rape victims. Expanding our skills development programs and economic empowerment initiatives to create sustainable livelihoods for community members.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="cta-section">
        <div className="container">
          <h2>Join Us in Making a Difference</h2>
          <p>Your support helps us continue our vital work in the Kayamandi community</p>
          <div className="cta-buttons">
            <a href="/donate" className="btn btn-primary">Donate Now</a>
            <a href="/contact" className="btn btn-outline">Get Involved</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VisionPage;
